// Progress: what you finished this year.
//
// THE RETROSPECTIVE'S ANSWER IS A SENTENCE, AND THEN THE CHARTS. "You finished
// 470 things in 2026, on 216 different days." goes above every figure and chart
// it summarizes, because the question this screen exists for has a one-line
// answer and a grid is not one (prototype-v1.md section 6). The daily quote
// above that year state is separate presentation; the tiles are the texture
// underneath the answer.
//
// A MISSED DAY IS A PALE SQUARE, NEVER A BROKEN CHAIN. There is no streak on
// this screen, in the payload behind it, or in the schema behind that
// (AGENTS.md, "Counts, never streaks") - the third figure is the BUSIEST MONTH,
// which is a count that only grows, and the architecture spike's `longest
// streak: 31 days` was overridden by the settled principle rather than ported.
// The line at the foot says so out loud, which is the prototype's own copy: a
// grid of squares with gaps in it is exactly the picture people read a streak
// into, so the screen states what it is not. Do not add a run-length anywhere,
// however easy it is to derive from what is already here.
//
// IT WRITES NOTHING, and it says nothing about writing. A tile is not a button:
// looking INSIDE a day is the month screen's job and that screen reaches every
// day of the year, so a control here would be a second door onto one thing. The
// one control that does point somewhere - "All reminders" on the reminders card
// - goes to a screen that already exists.
//
// EVERY FIGURE IS THE SERVER'S, and what one act COUNTS AS is decided once
// there - `COMPLETED_ITEM_ID` in `api/src/db/repositories/support.ts`, reaching
// this screen through `thingKey` in `api/src/domain/year.ts`. A five-step leg
// day is ONE thing finished, exactly as it is one item on the day, and a native
// client cannot phrase that differently. Same reason the day's `meta` and
// `schedules.cap` are computed there.
//
// THAT RULE SAYS WHICH ITEM A ROW COUNTS TOWARD, NOT WHETHER IT IS DONE, and
// that is the one hazard behind this screen: a part-finished checklist would
// count here and not on the day. It cannot happen today - no route creates a
// step - and it is NOT this screen's to answer. Do not add a rule for it here,
// and never write that the day and the year cannot disagree. The block over
// `thingKey` carries the whole of it.

import type { CSSProperties } from 'react';
import type { YearResponse, YearSchedule } from '../api/client.ts';
import { Icon } from '../day/Icon.tsx';
import { hueIn } from '../edit/edit-model.ts';
import { dayAndMonth } from '../iso-day.ts';
import { AccountButton } from '../shell/NavBar.tsx';
import type { Mode } from '../shell/theme.ts';
import { useMode } from '../shell/theme.ts';
import { useVoice, wording } from '../shell/voice.ts';
import { DailyQuote } from './DailyQuote.tsx';
import {
  busiestMonthName,
  emptyYearBody,
  gridLabel,
  gridTiles,
  isOver,
  isThisYear,
  labelledMonths,
  scheduleNote,
  yearHeadline,
} from './progress-model.ts';
import type { ProgressScreenState } from './useProgressScreen.ts';

interface Props {
  state: ProgressScreenState;
  /** Opens Settings, which on a phone has no other door from here. */
  onSettings: () => void;
  /** The reminders screen, which the reminders card points at. */
  onOpenReminders: () => void;
}

export function ProgressScreen({ state, onSettings, onOpenReminders }: Props) {
  const { view } = state;

  return (
    <div className="scroll">
      {/* WIDER THAN EVERY OTHER SCREEN, which is the prototype's own `.wrap
          wide` (1000px against 720px) and the reason the year grids go two-up
          on a desktop at all: a 53-column lattice inside a 720px reading column
          is a 6px tile. It changes nothing on a phone. */}
      <div className="wrap wide">
        <div className="toprow">
          <h1>Progress</h1>
          <AccountButton onClick={onSettings} />
        </div>

        {/* MOUNTED WHETHER OR NOT THE YEAR ARRIVED - the same invariant the
            month screen states for its own bar, and for the same reason: a 2025
            that could not be read must not remove the arrow back to the 2026
            that was. */}
        <YearBar state={state} />

        {/* In normal flow BELOW the year controls and ABOVE every year state:
            a change of quote cannot cover or reposition the controls, and a
            failed year read does not make the day's quote disappear with it. */}
        <DailyQuote />

        {/* The year is still drawn and a later read of it did not arrive, so
            this says WHICH of the two you are looking at rather than replacing
            figures that are very likely still right. The arrows and every tile
            keep working, which is the point of keeping them. */}
        {state.refreshFailure !== null && (
          <p className="sheetproblem" role="alert">
            This year may be out of date. {state.refreshFailure}
          </p>
        )}

        {view === null ? (
          // Whether a year is on its way is a state of its own, never inferred
          // from the absence of a reason it is not. `Try again` never appears
          // for a 401 - that raises the signed-out screen instead, and
          // re-sending a sessionless request is the dead end this client
          // already shipped once (web/src/README.md).
          <div className="daystate" role="status">
            {state.loading ? (
              <p>Loading your year…</p>
            ) : (
              <>
                <h2>That year is not here.</h2>
                <p>{state.failure}</p>
                <button className="btn" onClick={state.retry}>
                  Try again
                </button>
              </>
            )}
          </div>
        ) : (
          <YearBody view={view} onOpenReminders={onOpenReminders} />
        )}
      </div>
    </div>
  );
}

/**
 * Which year is on, and the way to the two either side of it.
 *
 * BEYOND THE APPROVED PROTOTYPE, which has one year and no way off it, and here
 * for a reason rather than by drift: this product keeps history for ever and
 * throws none of it away, so a retrospective that could only ever show the
 * current year would make every completed year permanently unreachable the
 * moment January arrived. That is the shape of hole an archived schedule had
 * until the Settings list closed it (web/src/README.md) - something real with
 * nowhere to be reached from - and it is treated here as the same kind of
 * defect rather than as a missing feature.
 *
 * Otherwise it is `MonthBar` exactly: named from the year being ASKED FOR so a
 * year that failed to arrive is named honestly, following the payload's own
 * neighbours whenever there is a payload, and offering the way back only when
 * you are somewhere else.
 */
function YearBar({ state }: { state: ProgressScreenState }) {
  const { year, previousYear, nextYear, thisYear } = state;

  // These three arrive together: the neighbours are derived from the year, so
  // the only state without them is the one before anything at all has come back
  // - the first read, and the failure of the first read. There is no year to
  // name there and nothing already read for a failure to take away.
  if (year === null || previousYear === null || nextYear === null) return null;

  return (
    <>
      {/* A COMPACT GROUP AT THE LEFT, and not the month screen's centred bar,
          which is the one place this deliberately departs from it. There the
          month IS the screen's subject and the bar is its title; here the
          subject is the sentence below and the year is the context it is true
          in, so a centred serif year under a left-aligned "Progress" would be a
          second title competing with the first. Left-aligned and sized to its
          own contents, it reads as what it is: a control.

          Both arrows carry the year they go to in their name: "Previous" alone
          makes a screen reader announce the same word on every year, and the
          whole point of the control is which one it lands on. */}
      <div className="yearbar">
        <button
          className="iconbtn"
          aria-label={`Go to ${previousYear}`}
          onClick={() => state.go(previousYear)}
        >
          <Icon name="chevL" />
        </button>
        {/* Which year is on, under the screen's own h1. Serif, because the date
            on the day screen and the month on the month screen both are. */}
        <h2 className="yearname tnum">{year}</h2>
        <button
          className="iconbtn"
          aria-label={`Go to ${nextYear}`}
          onClick={() => state.go(nextYear)}
        >
          <Icon name="chevR" />
        </button>

        {/* Only when you are somewhere else, because a control that returns you
            to where you already are is a control that appears to do nothing.
            "This year" and not the year's own figure, which is the vocabulary
            the day screen's chooser and the month's bar already use for the same
            idea - a button reading "2026" beside a heading reading "2025" is
            read as a picker rather than as the way back. */}
        {thisYear !== null && year !== thisYear && (
          <button
            className="btn yearback"
            aria-label={`This year, ${thisYear}`}
            onClick={() => state.go(thisYear)}
          >
            This year
          </button>
        )}
      </div>
    </>
  );
}

function YearBody({ view, onOpenReminders }: { view: YearResponse; onOpenReminders: () => void }) {
  const busiest = busiestMonthName(view);
  const nothing = view.finished === 0;
  // The stored hex is the light one; on a dark page a card paints the
  // prototype's dark counterpart instead (`edit/edit-model.ts`). Read once here
  // rather than in each card, so a year of schedules mounts one subscription.
  const mode = useMode();
  // The answer and the no-streaks line, which are the two sentences on this
  // screen. Every figure beside them - the two numerals, the busiest month, each
  // schedule's own total - is the server's count and is plain in both voices.
  const voice = useVoice();

  return (
    <>
      <span className="yearcontext">
        {isThisYear(view) ? 'This year so far' : `In ${view.year}`}
      </span>
      {/* An h3 under the year's own h2 in the bar, so the heading order has no
          gap in it - the same rule the month screen and the placeholders keep.
          It is the ANSWER to the screen's question and therefore a heading, not
          a caption. */}
      <h3 className="yearline">{yearHeadline(view, voice)}</h3>

      {!nothing && (
        <div className="yearstats">
          {/* The same two figures the sentence already gave, as numerals: the
              sentence is how you read it and these are how you glance at it,
              which is the prototype's own arrangement. */}
          <div className="yearstat">
            <b className="tnum">{view.finished}</b>
            <span>finished</span>
          </div>
          <div className="yearstat">
            <b className="tnum">{view.activeDays}</b>
            <span>active days</span>
          </div>
          {/* THE THIRD FIGURE, AND IT IS NOT A STREAK. See the block at the top
              of this file: a count that only grows, in the place the
              architecture spike put a longest run. */}
          {busiest !== null && (
            <div className="yearstat">
              <b>{busiest}</b>
              <span>busiest month</span>
            </div>
          )}
        </div>
      )}

      {nothing && (
        <div className="mempty">
          {/* An h4 under the sentence's h3. The words are in `progress-model.ts`
              because a year that is OVER needs different ones - see
              `emptyYearBody`, which is also where the reason nothing here names
              a control lives. */}
          <h4>{isOver(view) ? 'A quiet year.' : 'Nothing here yet.'}</h4>
          <p>{emptyYearBody(view)}</p>
        </div>
      )}

      {/* Kept separate from the empty-year sentence: the CURRENT year's active
          schedules have a card before their first completion, with zero and an
          empty sparse grid. Hiding every card whenever the account-wide count
          is zero would discard exactly that server answer. */}
      {(view.schedules.length > 0 || view.reminders.finished > 0) && (
        <div className="ycards">
          {/* A schedule DELETED FOREVER is keyed by its place in the list and
              not by its name: `assembleYear` emits one card per distinct scope
              in the log and reports every one of them with a null id, so two
              that were called the same thing would collide on a name. The list
              is the server's own order and is neither sorted nor filtered here,
              which is what makes the index a stable name for a row. */}
          {view.schedules.map((schedule, index) => (
            <ScheduleCard
              key={schedule.id ?? `gone-${index}`}
              view={view}
              schedule={schedule}
              mode={mode}
            />
          ))}
          {view.reminders.finished > 0 && (
            <RemindersCard view={view} onOpenReminders={onOpenReminders} />
          )}
        </div>
      )}

      {/* THE LINE THE PRODUCT IS NAMED BY, and the reason it is on screen rather
          than only in a design document: a grid of squares with gaps in it is
          the exact picture people have been trained to read a streak into, and
          this says what those gaps are before anybody reads them as a failure.
          Drawn on an empty year too, where it is the only thing standing between
          a page of nothing and a page of nothing that feels like an accusation. */}
      <div className="coach yearcoach">
        <Icon name="info" />
        <span>{wording('year.nostreaks', voice)}</span>
      </div>
    </>
  );
}

/** One schedule: its name, its total, its year of tiles, and one line about it. */
function ScheduleCard({
  view,
  schedule,
  mode,
}: {
  view: YearResponse;
  schedule: YearSchedule;
  mode: Mode;
}) {
  const lattice = gridTiles(view, schedule);
  const months = labelledMonths(lattice);
  const note = scheduleNote(view, schedule);

  return (
    <div
      className={schedule.archived || schedule.id === null ? 'ycard arch' : 'ycard'}
      // The hue arrives from the API as `colorHex` and is set here, exactly as
      // it is set on a card on the day: one declaration, and the tint is derived
      // in CSS, so the grid is right in whichever theme it lands in. Through
      // `hueIn` because the STORED hex is the light one, and a dark page paints
      // the prototype's dark counterpart of it.
      style={{ '--c': hueIn(schedule.colorHex, mode) } as CSSProperties}
    >
      <div className="ycard-head">
        <span className="chip">
          <Icon name={schedule.symbol} />
        </span>
        {/* An h4 under the year's sentence, which is an h3. */}
        <h4>{schedule.name}</h4>
        {schedule.archived && <span className="archtag">archived</span>}
        <span className="n tnum">{schedule.finished}</span>
      </div>

      {/* ONE LABEL FOR THE WHOLE PICTURE rather than 371 cells each announcing a
          date - a year grid read out cell by cell is twenty minutes of "nothing
          due", and the figures a person actually needs are in the heading above
          and the line below. `role="img"` is what makes the label stand for the
          contents instead of being read out before them. */}
      <div className="ygrid" role="img" aria-label={gridLabel(view, schedule)}>
        {lattice.tiles.map((tile, index) => (
          // Both classes when both are true, and the STYLESHEET decides which
          // wins: a future day that has already been ticked off is drawn as
          // finished rather than as faint, which is the same distinction the
          // month screen makes when it fills a future square's dots and still
          // draws it no bar. The ordering that produces that is at `.ygrid i` in
          // styles.css, said there rather than by leaving a class off here.
          <i
            key={tile.day ?? `pad-${index}`}
            className={
              [tile.level > 0 ? `f l${tile.level}` : '', tile.faint ? 'fut' : '']
                .filter(Boolean)
                .join(' ') || undefined
            }
          />
        ))}
      </div>

      <div className="ymonths" aria-hidden="true">
        {months.map((month) => (
          <span key={month.label} style={{ gridColumn: `${month.column} / span 8` }}>
            {month.label}
          </span>
        ))}
      </div>

      {note !== '' && <p className="ycard-foot">{note}</p>}

      {/* A year the lattice could not hold all of: a 366-day year that opens on
          a Saturday, and THE NEXT ONE IS 2028 - not the far-off curiosity it
          reads as. They come round every 28 years (2000, 2028, 2056), so this
          paragraph is two years away rather than a decade, and
          `web/test/progress-model.test.ts` names the year it appears in.

          Said rather than silently dropped: a grid that quietly loses the last
          day of a year is the kind of defect nobody notices for a decade.

          ONE SENTENCE, IN THE SINGULAR, because one day is the only figure that
          can reach it. The lattice is 371 cells and opens at most six days
          early, so a year of at most 366 days loses at most one - and
          `useProgressScreen` refuses any span that is not one calendar year, so
          `dropped` cannot arrive here larger. A plural branch here would be a
          sentence nobody could ever read, kept correct by nobody. */}
      {lattice.dropped > 0 && (
        <p className="ycard-foot">
          The last day of {view.year} is counted above but does not fit this grid.
        </p>
      )}
    </div>
  );
}

/**
 * Reminders: a count, the most recent few, and DELIBERATELY NO GRID.
 *
 * A 53-by-7 grid reads as rhythm - "did you keep this up" - and a reminder has
 * no rhythm to keep: it is written down once, finished once, and counted on the
 * one day it was finished on and never again. Drawing one as a grid would invite
 * exactly the reading the whole screen refuses. The prototype says this at the
 * foot of the card, in the user's own words, and that line is ported.
 */
function RemindersCard({
  view,
  onOpenReminders,
}: {
  view: YearResponse;
  onOpenReminders: () => void;
}) {
  return (
    <div className="ycard rem">
      <div className="ycard-head">
        <span className="chip">
          <Icon name="pin" />
        </span>
        <h4>Reminders</h4>
        <span className="n tnum">{view.reminders.finished}</span>
      </div>

      {view.reminders.recent.length > 0 && (
        <>
          <p className="steplabel">The most recent</p>
          <ul className="remrecent">
            {/* Keyed by PLACE and not by what it says: nothing stops two
                reminders being written down under one title and finished on one
                day, and a key made of the two would be the same key twice. */}
            {view.reminders.recent.map((reminder, index) => (
              <li key={`${index}-${reminder.day}`}>
                {/* The TITLE AS IT WAS when it was finished, which is what the
                    log holds - and what keeps this readable after the reminder
                    itself has been deleted. */}
                <span>{reminder.title}</span>
                <em>{dayAndMonth(reminder.day)}</em>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="ycard-foot">
        One-off things, finished once each. No grid here: a reminder has no rhythm to show, and a
        finished one is counted on one day and never again.
      </p>

      {/* The one control on this screen, and it goes somewhere that already
          exists. A count would need every OPEN reminder, which this payload does
          not carry and should not - it is a year of finished things. */}
      <button className="remfoot inline" onClick={onOpenReminders}>
        All reminders
        <Icon name="chevR" />
      </button>
    </div>
  );
}
